// Client-side courtesy validation for `objectlist` settings. The backend is the authority (it answers 422 on a
// violation); these helpers exist so the editor can flag a bad row *while it is being edited* instead of letting
// the admin discover the problem only after pressing "Speichern".

import {DeviceSettingObjectListItem} from './models/device-config.model';

/**
 * Reads an item's field as a finite number, or `null` when it is missing / not numeric.
 *
 * An `int` control emits `null` when the admin clears it, and a freshly parsed layer document may carry anything,
 * so every read has to cope with a non-number.
 */
export function numericField(item: DeviceSettingObjectListItem | undefined, key: string): number | null {
  if (!item) return null;
  const raw = item[key];
  return typeof raw === 'number' && Number.isFinite(raw) ? raw : null;
}

/**
 * Whether the row at {@link index} breaks the "strictly ascending by {@link key}" rule.
 *
 * A row is flagged when its own value is missing/non-numeric, or when it is not strictly greater than the
 * previous row's value. A row whose *predecessor* is itself unreadable is deliberately NOT flagged — the
 * predecessor already carries the error, and flagging both would be noise.
 *
 * @param items The current list value.
 * @param index The row to check.
 * @param key The item field that must ascend; `null` disables the rule.
 */
export function violatesAscending(
  items: readonly DeviceSettingObjectListItem[], index: number, key: string | null | undefined): boolean {
  if (!key) return false;

  const value = numericField(items[index], key);
  if (value === null) return true;
  if (index === 0) return false;

  const previous = numericField(items[index - 1], key);
  if (previous === null) return false;

  return value <= previous;
}

/** Whether ANY row of the list breaks the strictly-ascending rule (the save-blocking predicate). */
export function hasAscendingViolation(
  items: readonly DeviceSettingObjectListItem[], key: string | null | undefined): boolean {
  if (!key) return false;
  return items.some((_, index) => violatesAscending(items, index, key));
}
